import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    NgbDate,
    NgbDateAdapter,
    NgbDateNativeAdapter,
    NgbDateStruct,
} from '@cmps/ng-bootstrap';
import { PlacementArray } from '@cmps/ng-bootstrap/util/positioning';
import { endOfDay, startOfDay } from 'date-fns';

import { TranslationKeyMap } from './date-range.component.i18n';

@Component({
    selector: 'cmps-date-range',
    templateUrl: './date-range.component.html',
    host: {
        class: 'cmps-date-range',
    },
    providers: [{ provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }],
})
export class DateRangeComponent implements OnChanges, OnDestroy {
    /** @private */
    _i18n = TranslationKeyMap;

    @Input() dateRange: { from: Date; to: Date };
    @Output() dateRangeChange: EventEmitter<{ from: Date; to: Date }>;

    @Input() minFromDate: Date;
    @Input() maxFromDate: Date;

    @Input() minToDate: Date;
    @Input() maxToDate: Date;

    @Input() fromDatePlacement: PlacementArray;
    @Input() toDatePlacement: PlacementArray;

    /** @private */
    _minFromDate: NgbDateStruct | null;

    /** @private */
    _maxFromDate: NgbDateStruct | null;

    /** @private */
    _minToDate: NgbDateStruct | null;

    /** @private */
    _maxToDate: NgbDateStruct | null;

    /** @private */
    _fromDate: NgbDate | null;

    /** @private */
    _toDate: NgbDate | null;

    constructor(private readonly _ngbDateAdapter: NgbDateAdapter<Date>) {
        this.dateRangeChange = new EventEmitter();
        this._maxFromDate = this._ngbDateAdapter.fromModel(
            endOfDay(new Date()),
        );
        this._maxToDate = this._ngbDateAdapter.fromModel(endOfDay(new Date()));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dateRange?.currentValue != null) {
            this._fromDate = NgbDate.from(
                this._ngbDateAdapter.fromModel(this.dateRange.from),
            );
            this._toDate = NgbDate.from(
                this._ngbDateAdapter.fromModel(this.dateRange.to),
            );
        }
        if (changes.minFromDate != null) {
            this._minFromDate =
                this.minFromDate == null
                    ? null
                    : this._ngbDateAdapter.fromModel(
                          startOfDay(this.minFromDate),
                      );
        }
        if (changes.maxFromDate != null) {
            this._maxFromDate =
                this.maxFromDate == null
                    ? null
                    : this._ngbDateAdapter.fromModel(
                          endOfDay(this.maxFromDate),
                      );
        }
        if (changes.minToDate != null) {
            this._minToDate =
                this.minToDate == null
                    ? null
                    : this._ngbDateAdapter.fromModel(
                          startOfDay(this.minToDate),
                      );
        }
        if (changes.maxToDate != null) {
            this._maxToDate =
                this.maxToDate == null
                    ? null
                    : this._ngbDateAdapter.fromModel(endOfDay(this.maxToDate));
        }
    }

    ngOnDestroy(): void {
        this.dateRangeChange?.complete();
    }

    /** @private */
    _onFromChange(date: Date): void {
        const from = startOfDay(date);
        let to = endOfDay(
            new Date(
                this._toDate.year,
                this._toDate.month - 1,
                this._toDate.day,
            ),
        );
        if (from > to) {
            to = endOfDay(new Date(from));
            this._toDate = NgbDate.from(this._ngbDateAdapter.fromModel(to));
        }
        this.dateRangeChange.emit({ from, to });
    }

    /** @private */
    _onToChange(date: Date): void {
        let from = startOfDay(
            new Date(
                this._fromDate.year,
                this._fromDate.month - 1,
                this._fromDate.day,
            ),
        );
        const to = endOfDay(date);
        if (from > to) {
            from = startOfDay(new Date(to));
            this._fromDate = NgbDate.from(this._ngbDateAdapter.fromModel(from));
        }
        this._maxFromDate = this._ngbDateAdapter.fromModel(endOfDay(to));
        this.dateRangeChange.emit({ from, to });
    }
}
